/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, ember/no-observers, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { isArray } from '@ember/array';
import EmberObject, { action, computed } from '@ember/object';
import { or, filter, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import GradingMethod from 'linced-app/enums/grading-method';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import EditPostMixin from '@linc-technologies/linced-common/mixins/edit-post';
import moment from 'moment';
import { observes } from '@ember-decorators/object';
import { TrackedObject } from 'tracked-built-ins';
import {
  findBy,
  mapBy,
  sortBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';

export default class AssessmentController extends Controller.extend(
  EditPostMixin,
) {
  @action
  setFilteredModules(value) {
    this.filteredModules = value;
  }

  @action
  setNewAsOnDate(value) {
    this.newAsOnDate = value;
  }

  @action
  setNewFromDate(value) {
    this.newFromDate = value;
  }

  // Services
  @service
  personOptions;

  @service
  session;

  @service
  store;

  PersonType = PersonType;

  @alias('model.assessment')
  assessment;

  @alias('model.page')
  page;

  @alias('model.person')
  person;

  @alias('session.school')
  school;

  queryParams = ['asOnDate', 'fromDate'];

  get year() {
    if (isEmpty(this.asOnDate)) {
      return moment().year();
    }
    return moment(this.asOnDate).year();
  }

  @tracked today = moment().format('YYYY-MM-DD');

  @or('newAsOnDate', 'today')
  maxFromDate;

  @computed('today')
  get noFutureDates() {
    return [
      {
        validate: (inputValue) => {
          return isEmpty(inputValue) || inputValue <= this.today;
        },
        message: `Can't be in the future`,
      },
    ];
  }

  get fromDateValidations() {
    if (!isEmpty(this.newAsOnDate) && this.newAsOnDate < this.today) {
      return [
        {
          validate: (inputValue) => inputValue <= this.newAsOnDate,
          message: `Can't be after "on or before"`,
        },
      ];
    }

    return this.noFutureDates;
  }

  currentYear = moment().year();
  lastYear = moment().subtract(1, 'year').year();
  yearBeforeLast = moment().subtract(2, 'years').year();
  @tracked sortProp = 'person.firstName';
  @tracked sortDir = 'asc';
  @tracked
  postDialogueOpen = false;
  showNSN = false;
  autosaveNumericGrades = false;
  // Plain arrays, not A(): the route's setupController already seeds these
  // as plain arrays ([]), and selectLearner/editGrade/bulkEditGrades now
  // rebuild-and-reassign rather than mutate in place - see those actions.
  @tracked
  selectedLearners = [];
  @tracked
  bulkGrades = [];
  // auditReason holds the per-module "reason for change" text, keyed by module
  // id and seeded by the route. A TrackedObject so writing an arbitrary key
  // notifies, which is what `{{get this.auditReason module.id}}` relies on.
  auditReason = new TrackedObject({});

  @tracked filteredModules = [];

  // updateAuditReason writes one module's reason. The module id is curried in by
  // `{{fn}}` so the input's value arrives last.
  @action
  updateAuditReason(moduleId, value) {
    this.auditReason[moduleId] = value;
  }

  @filter(
    'bulkGrades',
    ['bulkGrades.[]', 'filteredModules.[]'],
    function (bulkGrade) {
      return this.filteredModules.includes(bulkGrade.module);
    },
  )
  filteredBulkGrades;

  @computed('model.group.currentLearners.[]', 'sortProp', 'sortDir')
  get sortedCurrentLearners() {
    // Sort sortedCurrentLearners on table sort feature.
    let sortedLearners = sortBy(
      this.get('model.group.currentLearners'),
      this.sortProp,
    );
    if (this.sortDir === 'desc') {
      return sortedLearners.reverse();
    }
    return sortedLearners;
  }

  @computed(
    'model.{grades.[],group.currentLearners.[],personId}',
    'filteredModules.@each.id',
    'fromDate',
  )
  get gradeMap() {
    let learners = this.get('model.group.currentLearners').reduce(
      (grades, member) => {
        grades[member.personId] = this.filteredModules.reduce((map, module) => {
          map[module.id] = [];
          return map;
        }, {});
        return grades;
      },
      {},
    );
    let fromTimestamp = 0;
    if (!isEmpty(this.fromDate)) {
      fromTimestamp = moment(this.fromDate).unix();
    }
    return this.model.grades.reduce((grades, grade) => {
      if (grade.createTime < fromTimestamp) {
        // Older than the selected "from date" - skip.
        return grades;
      }
      let personId = grade.get('person.id');
      let moduleId = grade.get('module.id');
      if (isEmpty(personId) || isEmpty(moduleId)) {
        return grades;
      }
      if (!isEmpty(grades[personId]) && isArray(grades[personId][moduleId])) {
        grades[personId][moduleId].push(grade);
      }
      return grades;
    }, learners);
  }

  @computed(
    'bulkGrades.[]',
    'filteredModules',
    'gradeMap',
    'selectedLearners.[]',
  )
  get selectedLearnerGradedModules() {
    if (isEmpty(this.bulkGrades)) {
      return [];
    }

    let today = moment().format('YYYY-MM-DD');

    return mapBy(
      this.bulkGrades.filter(
        (bulkGrade) =>
          findBy(this.filteredModules, 'id', bulkGrade.get('module.id')) &&
          this.selectedLearners.some((learnerId) =>
            findBy(
              this.gradeMap[learnerId][bulkGrade.get('module.id')],
              'date',
              today,
            ),
          ),
      ),
      'module.id',
    );
  }

  @computed('model.posts.[]')
  get todaysAssessmentPosts() {
    let latestPostsMap = {};

    this.get('model.posts').forEach((post) => {
      let personId = post.get('person_id');
      let currentLatestPost = latestPostsMap[personId];

      if (
        !currentLatestPost ||
        post.get('date') > currentLatestPost.post.get('date')
      ) {
        latestPostsMap[personId] = {
          personId: personId,
          post: post,
        };
      }
    });

    return Object.values(latestPostsMap);
  }

  // touchedGrades tracks which (personId, moduleId) grade cells have been
  // touched this session, as `${personId}:${moduleId}` keys. Rebuilt and
  // reassigned (never mutated in place) so the template's {{includes}}
  // read of this @tracked field picks up changes.
  @tracked
  touchedGrades = [];

  @observes('model.{assessment.modules.[],group.id,group.currentLearners.[]}')
  _resetTouchedGrades() {
    this.touchedGrades = [];
  }

  GradingMethod = GradingMethod;
  @tracked fromDate = '';
  @tracked newFromDate = '';
  @tracked asOnDate = '';
  @tracked newAsOnDate = '';

  @tracked
  selectedLearnerObjects;

  get datesChanged() {
    return (
      this.fromDate !== this.newFromDate || this.asOnDate !== this.newAsOnDate
    );
  }

  get isEditable() {
    return (
      isEmpty(this.asOnDate) ||
      moment(this.asOnDate).diff(moment(), 'days') === 0
    );
  }

  @computed('page.hasPosts')
  get hasPosts() {
    return this.page.get('hasPosts');
  }

  @action
  changeDates() {
    this.fromDate = this.newFromDate;
    this.asOnDate = this.newAsOnDate;
  }

  @action
  openPostDialogue(learner) {
    // Plain array: this becomes `this.person`, passed to <BulkPost
    // @person>. BulkPost's initialisePostData() used to call .removeObject()
    // directly on it, but now filters it into its own selectedPeople
    // instead of mutating the passed-in array, so this no longer needs to
    // be a genuine EmberArray.
    let selectedPeople = [];
    if (!learner) {
      this.sortedCurrentLearners.forEach((learner) => {
        if (this.selectedLearners.includes(learner.get('personId'))) {
          selectedPeople.push(learner.get('person'));
        }
      });
    }
    this.person = learner ? learner : selectedPeople;
    this.postDialogueOpen = true;
  }

  @action
  closePostDialogue() {
    this.person = null;
    this.selectedLearners = [];
    this.postDialogueOpen = false;
    this.get('model.posts').update();
  }

  @action
  changeYear(selectedYear) {
    let newAsOnDate = '';
    if (selectedYear === this.currentYear) {
      // Clear the "on or before" filter when selecting this year.
      newAsOnDate = '';
    } else {
      // Filter for grades "on or before" the end of the selected year.
      newAsOnDate = moment(selectedYear, 'YYYY')
        .endOf('year')
        .format('YYYY-MM-DD');
    }

    // Clear "on or after" date.
    this.fromDate = '';
    this.newFromDate = '';
    // Apply "on or before" date determined above.
    this.newAsOnDate = newAsOnDate;
    this.asOnDate = newAsOnDate;
  }

  @action
  refreshGrades() {
    this.get('model.grades').update();
  }

  @action
  markTouched(personId, moduleId, removeTouched) {
    let key = `${personId}:${moduleId}`;
    if (removeTouched) {
      this.touchedGrades = this.touchedGrades.filter((k) => k !== key);
    } else if (!this.touchedGrades.includes(key)) {
      this.touchedGrades = [...this.touchedGrades, key];
    }
    this.model.grades.update();
  }

  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  selectAll() {
    if (this.selectedLearners.length !== this.sortedCurrentLearners.length) {
      this.selectedLearners = this.sortedCurrentLearners.map(
        (item) => item.personId,
      );
    } else {
      this.selectedLearners = [];
    }
    if (
      this.selectedLearnerObjects.length !== this.sortedCurrentLearners.length
    ) {
      this.selectedLearnerObjects = this.sortedCurrentLearners.map(
        (item) => item.person,
      );
    } else {
      this.selectedLearnerObjects = [];
    }
  }

  @action
  selectLearner(learner) {
    // Rebuild-and-reassign, not mutate-in-place: the route's
    // setupController already seeds these as plain arrays, so the old
    // .removeObject/.addObject calls here were already throwing whenever a
    // user tried to select an individual learner.
    if (this.selectedLearners.includes(learner.personId)) {
      this.selectedLearners = this.selectedLearners.filter(
        (id) => id !== learner.personId,
      );
      this.selectedLearnerObjects = this.selectedLearnerObjects.filter(
        (person) => person !== learner.person,
      );
    } else {
      this.selectedLearners = [...this.selectedLearners, learner.personId];
      this.selectedLearnerObjects = [
        ...this.selectedLearnerObjects,
        learner.person,
      ];
    }
  }

  @action
  editGrade(grade, remove) {
    // Rebuild-and-reassign: bulkGrades is seeded as a plain array by the
    // route, so the old removeObject/addObject calls here were already
    // throwing the moment a grade was entered via AssessmentMultiGrader.
    let grades = this.bulkGrades.filter((g) => g.module !== grade.module);
    if (!remove) {
      grades = [...grades, EmberObject.create(grade)];
    }
    this.bulkGrades = grades;
  }

  @action
  bulkEditGrades() {
    if (this.bulkGrades.length && this.selectedLearners.length) {
      this.selectedLearners.forEach((learnerId) => {
        let existingGrades = this.gradeMap[learnerId];
        this.bulkGrades.forEach(async (bulkGrade) => {
          if (!findBy(this.filteredModules, 'id', bulkGrade.get('module.id'))) {
            // Module not currently visible.  Skip.
            return;
          }
          const newGrade = bulkGrade.get('grade');
          let latestGrade = sortBy(
            existingGrades[bulkGrade.get('module.id')],
            'createTime',
          ).at(-1);
          const grade = await this.getGrade({
            gradeType: bulkGrade.get('gradeType'),
            latestGrade,
            personId: findBy(
              this.model.group.currentLearners,
              'personId',
              learnerId,
            ).get('person'),
            module: bulkGrade.get('module'),
          });

          switch (bulkGrade.get('gradeType')) {
            case GradingMethod.SELECT:
              grade.set('selectAwarded', newGrade);
              break;
            case GradingMethod.MULTIPLE_SELECT:
              // Start with any existing grade(s).
              if (latestGrade && isArray(latestGrade.multiSelectAwarded)) {
                grade.multiSelectAwarded.pushObjects(
                  latestGrade.multiSelectAwarded.toArray(),
                );
              }

              // Append bulk selection(s).
              grade.multiSelectAwarded.pushObjects(newGrade);

              // Remove any duplicates.
              grade.set('multiSelectAwarded', uniq(grade.multiSelectAwarded));
              break;
            case GradingMethod.NUMERIC:
              grade.set('numericAwarded', newGrade);
              grade.set('numericScale', 0);
              break;
            case GradingMethod.ICON_SELECT:
              grade.set('iconAwarded', newGrade);
              break;
            default:
              break;
          }
          // Push audit reason if it's set.
          if (!isEmpty(this.auditReason[grade.module_id])) {
            grade.set('auditReason', this.auditReason[grade.module_id]);
          }
          grade.save().then(() => {
            this.markTouched(learnerId, bulkGrade.get('module.id'));
          });
        });
      });
      this.selectedLearners = [];
      this.bulkGrades = [];
    }
  }

  @action
  findLatestGrade(learnerGrades, module) {
    let moduleGrades = learnerGrades[module.id];
    if (moduleGrades && moduleGrades.length) {
      return moduleGrades
        .slice()
        .sort((a, b) => a.createTime - b.createTime)
        .at(-1);
    }
    return null;
  }

  @action
  toggleAutosaveNumericGrades() {
    // Toggle the property immediately, as the user preference is secondary.
    this.toggleProperty('autosaveNumericGrades');
    // Save the user preference, but don't worry about failures.
    this.personOptions.setAutosaveNumericGrades(this.autosaveNumericGrades);
  }
  async getGrade(data) {
    if (data.latestGrade) {
      let gradeDate = moment(data.latestGrade.get('date')).format('YYYY-MM-DD');
      if (gradeDate === moment().format('YYYY-MM-DD')) {
        // Latest grade was today, so we can update it.
        data.latestGrade.set('assessorId', this.get('session.person'));
        return data.latestGrade;
      }
    }
    return this.store.createRecord('assessments/grade', {
      schoolId: this.get('session.school'),
      // A belongsTo takes a record, not the async proxy.
      assessmentId: await data.module.get('assessment'),
      assessmentModuleId: data.module,
      assessorId: this.get('session.person'),
      personId: data.personId,
      gradeType: data.gradeType,
    });
  }
}
